import { cn } from "@/lib/utils";
import { deviationWidthPct } from "./chart-scale";

interface DeviationTrackProps {
  /** Desvio vs base, na unidade do indicador. Zero = empatou com a base. */
  value: number | null;
  /** Amplitude do eixo, compartilhada por todas as linhas (`deviationDomain`). */
  domain: number;
  /** Este desvio é uma MELHORA? Vem do registry de objetivos, nunca inferido da unidade. */
  positive: boolean;
  /** Texto do próprio valor — a codificação secundária que sustenta a cor. */
  label: string;
  ariaLabel: string;
}

/**
 * Uma faixa de desvio vs base: zero no centro, barra para o lado do sinal.
 *
 * Por que desvio e não valor absoluto: os candidatos ficam a décimos de ponto uns dos outros. Num
 * eixo ancorado no zero as barras são idênticas e o ranking vira uma coluna de números repetidos —
 * que foi exatamente o que a tela mostrava. O zero aqui é a configuração ATUAL, e é dela que a
 * decisão depende.
 *
 * O rótulo anda colado à ponta da barra em vez de morar numa coluna fixa — pinado à borda, ele
 * deixava um vão morto entre a barra e o número que a descreve.
 */
export function DeviationTrack({ value, domain, positive, label, ariaLabel }: DeviationTrackProps) {
  const width = value == null ? 0 : deviationWidthPct(value, domain);
  const toLeft = (value ?? 0) < 0;
  const tone = positive ? "bg-rd-success-500" : "bg-rd-danger-500";
  const textTone = positive ? "text-rd-success-600" : "text-rd-danger-600";

  return (
    <span className="flex h-4 w-full items-center" role="img" aria-label={ariaLabel}>
      <span className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
        {value == null || !toLeft ? null : (
          <>
            <Value tone={textTone}>{label}</Value>
            <Bar width={width} tone={tone} />
          </>
        )}
      </span>

      {/* Espaçador de 1px na posição do zero. A régua VISÍVEL (tracejada) é desenhada uma vez
          atrás de todas as linhas (ver `CandidateRanking`) — por linha ela ficaria escondida
          atrás da própria barra. Este espaçador é o que garante que barra e régua caiam no
          mesmo pixel. */}
      <span className="w-px shrink-0" aria-hidden />

      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        {value == null ? (
          <span className="pl-1.5 font-mono text-[10px] text-text-tertiary">—</span>
        ) : toLeft ? null : (
          <>
            <Bar width={width} tone={tone} />
            <Value tone={textTone}>{label}</Value>
          </>
        )}
      </span>
    </span>
  );
}

/**
 * O lugar da faixa de um candidato que NÃO tem número: um trilho apagado, do tamanho
 * do trilho de verdade. Some-lo deixaria a linha do candidato que falhou parecida com a de um
 * candidato que empatou com a base — que é uma leitura oposta.
 */
export function DeviationPlaceholder({ label }: { label?: string }) {
  return (
    <span
      className="flex h-4 w-full items-center"
      role="img"
      aria-label={label ?? "não avaliado — sem resultado"}
    >
      <span
        className={cn(
          "flex flex-1 items-center justify-center rounded-[3px] bg-bg-muted-rd text-[10px] text-text-tertiary",
          label ? "h-4" : "h-2.5"
        )}
      >
        {label}
      </span>
    </span>
  );
}

function Bar({ width, tone }: { width: number; tone: string }) {
  return (
    <span
      // O teto protege o rótulo colado à ponta: sem ele, a barra mais longa empurraria o número
      // para fora do trilho, por cima do nome do candidato (que já vem truncado).
      className={cn("h-2.5 w-[var(--dev-w)] max-w-[calc(100%-3rem)] shrink-0 rounded-[3px]", tone)}
      style={{ "--dev-w": `${width}%` } as React.CSSProperties}
    />
  );
}

function Value({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span className={cn("shrink-0 font-mono text-[10px] tabular-nums", tone)}>{children}</span>
  );
}
